import React from 'react';
import { TableHelper } from 'common/components/table-react/types';
import { Experiment, ExperimentStatus, FlattenedExperimentMetricResults, StatisticallySignificant } from 'apps/experiments/src/types';
import { LocalizationStore } from 'common/i18n/store';
import { computeStatus, computeStatusPercent } from 'iron/apps/experiments/src/components/utils';
import { Name, StatsigChip, StatsigChipWrapper, Status, UserInfo } from './cell-renderers';
import styles from './custom-cell-types.module.styl';
import { ExperimentTableCellType } from './types';
export { ExperimentTableCellType };
export const EMPTY_CELL_LABEL = "\u2013";
export const STATUS_ORDER = {
  [ExperimentStatus.DRAFT]: 4,
  [ExperimentStatus.CONFIGURED]: 3,
  [ExperimentStatus.ACTIVE]: 2,
  [ExperimentStatus.CONCLUDED]: 1,
  [ExperimentStatus.SUCCESS]: 5,
  [ExperimentStatus.FAIL]: 6
};
const EMPHASIZED_STATUSES = new Set([ExperimentStatus.DRAFT, ExperimentStatus.CONFIGURED, ExperimentStatus.ACTIVE, ExperimentStatus.CONCLUDED]);
const NO_RESULTS_STATUSES = new Set([ExperimentStatus.DRAFT, ExperimentStatus.CONFIGURED, ExperimentStatus.ACTIVE]);
const PERCENT_FORMATTER = new Intl.NumberFormat("default", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
export const customCellTypesCallback = <T extends Experiment,>(tableHelper: TableHelper<T>, i18n: LocalizationStore) => {
  return {
    [ExperimentTableCellType.Name]: tableHelper.cellType<string>({
      template: (value, info) => {
        const experiment = info.cellContext.row.original;
        const status: ExperimentStatus = computeStatus(experiment);
        const hypothesis = experiment.hypothesis || "";
        const featureFlag = experiment.feature_flag;
        let description = hypothesis;
        if (!description) {
          var _featureFlag$ruleset;
          const variantsCount = featureFlag === null || featureFlag === void 0 || (_featureFlag$ruleset = featureFlag.ruleset) === null || _featureFlag$ruleset === void 0 || (_featureFlag$ruleset = _featureFlag$ruleset.variants) === null || _featureFlag$ruleset === void 0 ? void 0 : _featureFlag$ruleset.length;
          if (variantsCount) {
            description = i18n.t("experiments.list.variants", {
              count: variantsCount
            });
          } else {
            var _experiment$exposures;
            const exposuresVariants = Object.keys((_experiment$exposures = experiment === null || experiment === void 0 ? void 0 : experiment.exposures_cache) !== null && _experiment$exposures !== void 0 ? _experiment$exposures : []).length - 1; // exclude $overall
            if (exposuresVariants > 1) {
              description = i18n.t("experiments.list.variants", {
                count: exposuresVariants
              });
            }
          }
        }
        if (!description) {
          description = i18n.t("experiments.list.no-description");
        }
        return <Name name={value} description={description} isEmphasized={EMPHASIZED_STATUSES.has(status)} />;
      }
    }),
    [ExperimentTableCellType.UserInfo]: tableHelper.cellType<string>({
      template: (_, info, params) => {
        var _params$getUserInfo, _params$getUserInfo2;
        const userIconAttrs = (_params$getUserInfo = params === null || params === void 0 || (_params$getUserInfo2 = params.getUserInfo) === null || _params$getUserInfo2 === void 0 ? void 0 : _params$getUserInfo2.call(params, info.cellContext.row)) !== null && _params$getUserInfo !== void 0 ? _params$getUserInfo : {};
        return <UserInfo user={userIconAttrs} />;
      }
    }),
    [ExperimentTableCellType.Status]: tableHelper.cellType<string>({
      sortRankTransform: (_, context) => {
        var _STATUS_ORDER$status;
        const sortDirection = context.column.getIsSorted();
        const experiment = context.row.original;
        const status = computeStatus(experiment);
        return (_STATUS_ORDER$status = STATUS_ORDER[status]) !== null && _STATUS_ORDER$status !== void 0 ? _STATUS_ORDER$status : sortDirection === "asc" ? 7 : 0;
      },
      template: (_, info) => {
        var _info$cellContext$row;
        const status = computeStatus(info.cellContext.row.original);
        const percent = computeStatusPercent(info.cellContext.row.original);
        const variant = (_info$cellContext$row = info.cellContext.row.original) === null || _info$cellContext$row === void 0 || (_info$cellContext$row = _info$cellContext$row.results_cache) === null || _info$cellContext$row === void 0 ? void 0 : _info$cellContext$row.variant;
        return <Status status={status} percent={percent} variant={variant} />;
      }
    }),
    [ExperimentTableCellType.Results]: tableHelper.cellType<string>({
      template: (_, info) => {
        var _experiment$results_c, _experiment$results_c2, _summary$Statisticall, _summary$Statisticall2, _summary$Statisticall3, _summary$Statisticall4;
        const experiment = info.cellContext.row.original;
        const status = computeStatus(experiment);
        if (NO_RESULTS_STATUSES.has(status)) {
          return <div className={styles.lightText}>{EMPTY_CELL_LABEL}</div>;
        }
        const summary: Partial<Record<StatisticallySignificant, FlattenedExperimentMetricResults[]>> = (_experiment$results_c = (_experiment$results_c2 = experiment.results_cache) === null || _experiment$results_c2 === void 0 ? void 0 : _experiment$results_c2.summary) !== null && _experiment$results_c !== void 0 ? _experiment$results_c : {};
        const signifantPositiveCount = (_summary$Statisticall = (_summary$Statisticall2 = summary[StatisticallySignificant.YES_POSITIVE]) === null || _summary$Statisticall2 === void 0 ? void 0 : _summary$Statisticall2.length) !== null && _summary$Statisticall !== void 0 ? _summary$Statisticall : 0;
        const signifantNegativeCount = (_summary$Statisticall3 = (_summary$Statisticall4 = summary[StatisticallySignificant.YES_NEGATIVE]) === null || _summary$Statisticall4 === void 0 ? void 0 : _summary$Statisticall4.length) !== null && _summary$Statisticall3 !== void 0 ? _summary$Statisticall3 : 0;
        if (signifantPositiveCount > 0 && signifantNegativeCount > 0) {
          return <StatsigChipWrapper>
              <StatsigChip label={i18n.t("experiments.list.significant-positive-short", {
              count: signifantPositiveCount
            })} statsig={StatisticallySignificant.YES_POSITIVE} />
              <StatsigChip label={i18n.t("experiments.list.significant-negative-short", {
              count: signifantNegativeCount
            })} statsig={StatisticallySignificant.YES_NEGATIVE} />
            </StatsigChipWrapper>;
        }
        if (signifantPositiveCount > 1) {
          return <StatsigChip label={i18n.t("experiments.list.significant-positive", {
            count: signifantPositiveCount
          })} statsig={StatisticallySignificant.YES_POSITIVE} />;
        }
        if (signifantNegativeCount > 1) {
          return <StatsigChip label={i18n.t("experiments.list.significant-negative", {
            count: signifantNegativeCount
          })} statsig={StatisticallySignificant.YES_NEGATIVE} />;
        }
        if (signifantPositiveCount === 1) {
          var _summary$Statisticall5, _experiment$metrics$f;
          const metricResult = (_summary$Statisticall5 = summary[StatisticallySignificant.YES_POSITIVE]) === null || _summary$Statisticall5 === void 0 ? void 0 : _summary$Statisticall5[0];
          if (!metricResult) {
            return null;
          }
          const metricName = (_experiment$metrics$f = experiment.metrics.find(m => m.id === metricResult.metricId)) === null || _experiment$metrics$f === void 0 ? void 0 : _experiment$metrics$f.name;
          return <StatsigChip label={"".concat(PERCENT_FORMATTER.format(Math.abs(metricResult.lift)), " \xB7 ").concat(metricName)} statsig={StatisticallySignificant.YES_POSITIVE} />;
        }
        if (signifantNegativeCount === 1) {
          var _summary$Statisticall6, _experiment$metrics$f2;
          const metricResult = (_summary$Statisticall6 = summary[StatisticallySignificant.YES_NEGATIVE]) === null || _summary$Statisticall6 === void 0 ? void 0 : _summary$Statisticall6[0];
          if (!metricResult) {
            return null;
          }
          const metricName = (_experiment$metrics$f2 = experiment.metrics.find(m => m.id === metricResult.metricId)) === null || _experiment$metrics$f2 === void 0 ? void 0 : _experiment$metrics$f2.name;
          return <StatsigChip label={"".concat(PERCENT_FORMATTER.format(Math.abs(metricResult.lift)), " \xB7 ").concat(metricName)} statsig={StatisticallySignificant.YES_NEGATIVE} />;
        }
        return <div className={styles.lightText}>{i18n.t("experiments.results.no-significance")}</div>;
      }
    }),
    [ExperimentTableCellType.LightText]: tableHelper.cellType<string>({
      template: (value: string) => {
        return <div className={styles.lightText}>{value}</div>;
      }
    })
  };
};